import { useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import {
  useLoginMutation,
  useResendLoginOtpMutation,
  useVerifyLoginOtpMutation,
} from "../api/auth";
import { useNavigate } from "react-router-dom";
import { useEstablishSession } from "../hooks/useEstablishSession";
import { Card, Label, TextInput, Button } from "flowbite-react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { toast } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import LoginOtpForm from "../components/LoginOtpForm";

/** The challenge the gateway hands back instead of a session when OTP is on. */
/** Unset in a build means no captcha (e.g. networks that can't reach Cloudflare). */
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

interface OtpChallenge {
  otp_token: string;
  expires_at: string | null;
  destination: string | null;
}

export default function LoginPage() {
  const [emailOrUsername, setemailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const [verifyLoginOtp] = useVerifyLoginOtpMutation();
  const [resendLoginOtp] = useResendLoginOtpMutation();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");

  // Set when the gateway answers the password step with a challenge instead of
  // a session; while it is set the card shows the code form. The password stays
  // in state throughout: the MSRP handshake still needs the plaintext once the
  // code is accepted, and it is never re-sent to the gateway.
  // Turnstile tokens are single-use, so every failed attempt needs a new one.
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const { establishSession, setIsAuthenticated } = useEstablishSession();

  /**
   * Turns an authenticated agent payload into a live session. Shared by the
   * password-only path and the OTP path, which reach it with the same body.
   */
  const startSession = async (agent: any, username: string, secret: string) => {
    // The MSRP digest runs on the user-typed plaintext, not on
    // `response.agent_password` — that field is a stored HA1 ("h1_<hash>")
    // and feeding it back in would double-hash. HA1 is recomputed in
    // msrpAuth from this plaintext plus the realm SCont returns in the 401.
    const result = await establishSession(agent, { username, secret });

    if (result === "reloading") return;

    navigate("/");
    finalizeLogin(true);
  };

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    setLoginError(null);

    try {
      const response = await login({
        agent_email_or_username: username,
        agent_password: password,
        ...(captchaToken && { captcha_token: captchaToken }),
      }).unwrap();

      if (response?.otp_required) {
        setOtpChallenge({
          otp_token: response.otp_token,
          expires_at: response.expires_at ?? null,
          destination: response.destination ?? null,
        });
        setOtpError(null);
        return;
      }

      await startSession(response, username, password);
    } catch (error: any) {
      console.error("Error during login process:", error);
      toast.error(t("login.toast.error"));
      setLoginError(t("login.toast.error"));
      finalizeLogin(false);
      setIsAuthenticated(false);
      setCaptchaToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (activation_code: string) => {
    if (!otpChallenge) return;
    setVerifying(true);
    setOtpError(null);

    try {
      const response = await verifyLoginOtp({
        otp_token: otpChallenge.otp_token,
        activation_code,
      }).unwrap();

      await startSession(response, emailOrUsername, password);
    } catch (error: any) {
      console.error("Error during OTP verification:", error);
      // The gateway distinguishes a wrong code from an expired or exhausted
      // challenge; showing its message is what tells the agent whether to
      // retype or to start over.
      setOtpError(error?.data?.message ?? t("login.otp.error"));
      setIsAuthenticated(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpChallenge) return;
    setResending(true);
    setOtpError(null);

    try {
      const response = await resendLoginOtp({
        otp_token: otpChallenge.otp_token,
      }).unwrap();
      setOtpChallenge({
        otp_token: response.otp_token,
        expires_at: response.expires_at ?? null,
        destination: response.destination ?? null,
      });
      toast.success(t("login.otp.resent"));
    } catch (error: any) {
      setOtpError(error?.data?.message ?? t("login.otp.resendError"));
    } finally {
      setResending(false);
    }
  };

  /** Abandons the challenge and returns to the credentials form. */
  const handleBackToCredentials = () => {
    setOtpChallenge(null);
    setOtpError(null);
    setPassword("");
    setCaptchaToken(null);
  };

  const finalizeLogin = (isSuccess: boolean) => {
    setLoading(false);
    if (!isSuccess) setLoginError(t("login.error"));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      toast.error(t("login.toast.passwordEmptyError"));
      return;
    }
    if (!emailOrUsername.trim()) {
      toast.error(t("login.toast.emailEmptyError"));
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error(t("login.toast.captchaError"));
      return;
    }
    handleLogin(emailOrUsername, password);
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
      <div className="absolute bottom-5 right-5 text-xs text-gray-500 dark:text-gray-400">
        version 2.9.0
      </div>
      <a href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
        <img alt="ICONT" src="/images/icont.png" className="mr-3 h-12" />
      </a>
      <Card
        horizontal
        imgSrc="/images/loginpage.png"
        imgAlt="ICONT"
        className="w-full md:max-w-[1024px] md:[&>*]:w-full md:[&>*]:p-16 [&>img]:hidden md:[&>img]:w-96 md:[&>img]:p-0 lg:[&>img]:block"
      >
        {otpChallenge ? (
          <LoginOtpForm
            destination={otpChallenge.destination}
            expiresAt={otpChallenge.expires_at}
            submitting={verifying}
            resending={resending}
            error={otpError}
            onSubmit={handleVerifyOtp}
            onResend={handleResendOtp}
            onBack={handleBackToCredentials}
          />
        ) : (
          <>
            <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">
              {t("login.title")}
            </h1>
            <form onSubmit={handleSubmit}>
              <div className="mb-4 flex flex-col gap-y-3">
                <Label htmlFor="username">{tCommon("form.userName")}</Label>
                <TextInput
                  id="username"
                  name="username"
                  placeholder={tCommon("form.emailOrUsernamePlaceholder")}
                  type="text"
                  value={emailOrUsername}
                  onChange={(e) => setemailOrUsername(e.target.value)}
                />
              </div>
              <div className="mb-6 flex flex-col gap-y-3">
                <Label htmlFor="password">{tCommon("form.password")}</Label>
                <div className="relative">
                  <TextInput
                    id="password"
                    name="password"
                    placeholder={tCommon("form.password")}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400"
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <HiEyeOff className="h-5 w-5" />
                    ) : (
                      <HiEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <div className="mb-4 text-right">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-primary-600 hover:underline dark:text-primary-300"
                >
                  {t("forgotPassword.link")}
                </button>
              </div>
              {TURNSTILE_SITE_KEY && (
                <div className="mb-4">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    options={{ size: "flexible" }}
                    onSuccess={setCaptchaToken}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                </div>
              )}
              {loginError && (
                <p className="mb-4 text-center text-red-500">{loginError}</p>
              )}
              <div className="mb-6">
                <Button
                  type="submit"
                  className="bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:bg-gradient-to-bl focus:ring-blue-300 dark:focus:ring-blue-800"
                  disabled={loading || (!!TURNSTILE_SITE_KEY && !captchaToken)}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="mr-2 h-5 w-5 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        ></path>
                      </svg>
                      {t("login.isSubmittingText")}
                    </div>
                  ) : (
                    t("login.submitButton")
                  )}
                </Button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-300">
                {t("login.notRegistered")}&nbsp;
                <button
                  onClick={() => navigate("/signup")}
                  className="text-primary-600 dark:text-primary-300"
                >
                  {t("login.createAccount")}
                </button>
              </p>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
